import React from "react";
import { Tag } from "../ui";
import PriceDisplay from "../PriceDisplay";
import { getImageUrl } from "../../utils/utils";
import {
  getOrderLineProductName,
  getModifierLineDisplayLabel,
} from "../../utils/orderLineLabels";
import { formatTrackHubError } from "../../utils/formatTrackHubError";

/**
 * OrderDetailsTab — Figma Onehub Copy node 1:3720
 */
const OrderDetailsTab = ({ orderDetails, t, isRTL }) => {
  if (!orderDetails) return null;

  const lineItemsSubtotal = (orderDetails.products || []).reduce(
    (sum, p) => sum + (Number(p.final_price) || 0),
    0
  );

  const isKeeta = String(orderDetails.platform || "").toLowerCase() === "keeta";
  const isJahez = String(orderDetails.platform || "")
    .toUpperCase()
    .includes("JAHEZ");

  const modifierOptionPrice = (opt) => {
    if (isJahez) return Number(opt.original_price ?? opt.final_price) || 0;
    return Number(opt.final_price) || 0;
  };

  const endCellClass = `oh-order-drawer-products-grid__cell--end${
    isRTL ? " oh-order-drawer-products-grid__cell--rtl" : ""
  }`;

  const priceClass = "oh-order-drawer-price-value";
  const priceBoldClass =
    "oh-order-drawer-price-value oh-order-drawer-price-value--bold";

  const discountAmount = Number(orderDetails.offer?.amount) || 0;

  return (
    <div className="oh-order-drawer-stack">
      <div className="oh-order-drawer-section-title">
        {t("ordersummary", "Order Summary")}
      </div>

      <div className="oh-order-drawer-card oh-order-drawer-card--scroll">
        <div className="oh-order-drawer-products-grid oh-order-drawer-products-grid--head">
          <div>{t("products", "Product")}</div>
          <div className={endCellClass}>{t("Modifiers", "Modifiers")}</div>
          <div className={endCellClass}>{t("Unit Price", "Unit Price")}</div>
          <div className={endCellClass}>{t("total", "Total")}</div>
        </div>

        <div className="oh-order-drawer-product-list">
          {(orderDetails.products || []).map((product, idx) => {
            const hasImage = Boolean(product.product_details?.image_path);
            const modifierLines = (product.modifiers || []).flatMap(
              (modifier, mIdx) =>
                (modifier.options || []).map((option, oIdx) => ({
                  key: `${mIdx}-${oIdx}`,
                  quantity: option.quantity,
                  label: getModifierLineDisplayLabel(modifier, option, isRTL),
                }))
            );

            return (
              <div key={idx} className="oh-order-drawer-product-item">
                <div className="oh-order-drawer-products-grid oh-order-drawer-products-grid--row">
                  <div className="oh-order-drawer-product-row">
                    {hasImage ? (
                      <img
                        src={getImageUrl(product.product_details.image_path)}
                        alt=""
                      />
                    ) : (
                      <div
                        className="oh-order-drawer-product-row__placeholder"
                        aria-hidden="true"
                      />
                    )}
                    <div className="oh-order-drawer-product-text">
                      <div className="oh-order-drawer-product-title-row">
                        <Tag
                          variant="neutral"
                          className="oh-order-drawer-qty-tag"
                        >
                          {product.quantity}
                        </Tag>
                        <div className="oh-order-drawer-product-name">
                          {getOrderLineProductName(product, isRTL)}
                        </div>
                      </div>
                      {modifierLines.length > 0 && (
                        <div className="oh-order-drawer-product-modifiers">
                          {modifierLines.map((line) => (
                            <div
                              key={line.key}
                              className="oh-order-drawer-modifier"
                            >
                              {line.quantity > 0 ? `+${line.quantity}` : ""}{" "}
                              {line.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={endCellClass}>
                    <PriceDisplay
                      value={Number(
                        product.modifiers?.reduce(
                          (acc, mod) =>
                            acc +
                            (mod.options || []).reduce(
                              (oAcc, opt) => oAcc + modifierOptionPrice(opt),
                              0
                            ),
                          0
                        ) || 0
                      )}
                      iconSize="12"
                      color="#030f1b"
                      className={priceClass}
                    />
                  </div>

                  <div className={endCellClass}>
                    <PriceDisplay
                      value={Number(product.original_price) || 0}
                      iconSize="12"
                      color="#030f1b"
                      className={priceClass}
                    />
                  </div>

                  <div className={endCellClass}>
                    <PriceDisplay
                      value={Number(product.final_price) || 0}
                      iconSize="12"
                      color="#030f1b"
                      className={priceBoldClass}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="oh-order-drawer-card">
        <div className="oh-order-drawer-note-label">
          {t("note", "Note")}
        </div>
        <div className="oh-order-drawer-note-text">
          {orderDetails.notes || t("noNotes", "No notes for this order")}
        </div>
      </div>

      <div className="oh-order-drawer-card oh-order-drawer-totals">
        <div className="oh-order-drawer-price-row">
          <span className="oh-order-drawer-price-row__label">
            {t("subtotal", "Subtotal")}
          </span>
          <PriceDisplay
            value={lineItemsSubtotal}
            iconSize="12"
            color="#030f1b"
            className={priceClass}
          />
        </div>

        {(orderDetails.customer_fees?.delivery_fee ?? 0) > 0 ? (
          <div className="oh-order-drawer-price-row">
            <span className="oh-order-drawer-price-row__label">
              {t("deliveryfee", "Delivery Fee")}
            </span>
            <PriceDisplay
              value={orderDetails.customer_fees.delivery_fee}
              iconSize="12"
              color="#030f1b"
              className={priceClass}
            />
          </div>
        ) : null}

        {(orderDetails.customer_fees?.tips ?? 0) > 0 ||
        (orderDetails.customer_fees?.top_up_amount ?? 0) > 0 ||
        isKeeta ? (
          <>
            {isKeeta && (
              <div className="oh-order-drawer-price-row">
                <span className="oh-order-drawer-price-row__label">
                  {t("platformFees", "Platform fees")}
                </span>
                <PriceDisplay
                  value={orderDetails.merchant_fees?.platform_service_fee ?? 0}
                  iconSize="12"
                  color="#030f1b"
                  className={priceClass}
                />
              </div>
            )}
            {orderDetails.customer_fees?.tips != null && (
              <div className="oh-order-drawer-price-row">
                <span className="oh-order-drawer-price-row__label">
                  {t("tips", "Tips")}
                </span>
                <PriceDisplay
                  value={orderDetails.customer_fees?.tips ?? 0}
                  iconSize="12"
                  color="#030f1b"
                  className={priceClass}
                />
              </div>
            )}
            {orderDetails.customer_fees?.top_up_amount != null && (
              <div className="oh-order-drawer-price-row">
                <span className="oh-order-drawer-price-row__label">
                  {t("Top-up Amount", "Top-up Amount")}
                </span>
                <PriceDisplay
                  value={orderDetails.customer_fees?.top_up_amount ?? 0}
                  iconSize="12"
                  color="#030f1b"
                  className={priceClass}
                />
              </div>
            )}
          </>
        ) : null}

        <div className="oh-order-drawer-price-row">
          <span className="oh-order-drawer-price-row__label">
            {t("discount", "Discount")}
          </span>
          <div className="oh-order-drawer-discount">
            {discountAmount > 0 && !isRTL ? <span>-</span> : null}
            <PriceDisplay
              value={discountAmount}
              iconSize="12"
              color="#030f1b"
              className={priceClass}
            />
            {discountAmount > 0 && isRTL ? <span>-</span> : null}
          </div>
        </div>

        <div className="oh-order-drawer-price-row oh-order-drawer-price-row--total">
          <span className="oh-order-drawer-price-row__label">
            {t("total", "Total")}
          </span>
          <PriceDisplay
            value={orderDetails.final_price}
            iconSize="12"
            color="#030f1b"
            className={priceBoldClass}
          />
        </div>
      </div>

      {orderDetails.trackhub?.shipmentId || orderDetails.trackhub?.status || orderDetails.trackhub?.lastError || orderDetails.trackhub?.lastErrorMessage ? (
        <>
          <div className="oh-order-drawer-section-title">
            {t("shipment", "Shipment")}
          </div>
          <div className="oh-order-drawer-card oh-order-drawer-card--trackhub">
            {(() => {
              const th = orderDetails.trackhub || {};
              const formatLocation = (loc) => {
                if (!loc) return "";
                if (typeof loc === "string") return loc.trim();
                const address = String(loc.address || "").trim();
                if (address) return address;
                const lat = Number(loc.lat);
                const lng = Number(loc.lng);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                }
                return "";
              };
              const logisticCompany = String(th.carrier || "").trim();
              const pickupLocation =
                formatLocation(th.pickup) ||
                String(orderDetails.branchName || orderDetails.branch_name || "").trim();
              const dropoffLocation =
                formatLocation(th.dropoff) ||
                String(
                  orderDetails.delivery_address ||
                    orderDetails.address_geo?.address ||
                    ""
                ).trim();
              const deliveryFeeRaw =
                orderDetails.customer_fees?.delivery_fee ??
                th.quote?.amount ??
                null;
              const deliveryFee =
                deliveryFeeRaw != null &&
                Number.isFinite(Number(deliveryFeeRaw)) &&
                Number(deliveryFeeRaw) >= 0
                  ? Number(deliveryFeeRaw)
                  : null;

              return (
                <>
                  {logisticCompany ? (
                    <div className="oh-order-drawer-price-row">
                      <span className="oh-order-drawer-price-row__label">
                        {t("logisticCompany", "Logistic Company")}
                      </span>
                      <span className="oh-order-drawer-price-row__value">
                        {logisticCompany}
                      </span>
                    </div>
                  ) : null}
                  {pickupLocation ? (
                    <div className="oh-order-drawer-price-row oh-order-drawer-price-row--multiline">
                      <span className="oh-order-drawer-price-row__label">
                        {t("pickupLocation", "Pick Up Location")}
                      </span>
                      <span className="oh-order-drawer-price-row__value">
                        {pickupLocation}
                      </span>
                    </div>
                  ) : null}
                  {dropoffLocation ? (
                    <div className="oh-order-drawer-price-row oh-order-drawer-price-row--multiline">
                      <span className="oh-order-drawer-price-row__label">
                        {t("dropoffLocation", "Drop Off Location")}
                      </span>
                      <span className="oh-order-drawer-price-row__value">
                        {dropoffLocation}
                      </span>
                    </div>
                  ) : null}
                  {deliveryFee != null ? (
                    <div className="oh-order-drawer-price-row">
                      <span className="oh-order-drawer-price-row__label">
                        {t("deliveryFee", "Delivery Fee")}
                      </span>
                      <PriceDisplay
                        value={deliveryFee}
                        iconSize="12"
                        color="#030f1b"
                        className={priceClass}
                      />
                    </div>
                  ) : null}
                  {th.status ? (
                    <div className="oh-order-drawer-price-row">
                      <span className="oh-order-drawer-price-row__label">
                        {t("shipmentStatus", "Status")}
                      </span>
                      <Tag>{th.status}</Tag>
                    </div>
                  ) : null}
                  {th.shipmentId ? (
                    <div className="oh-order-drawer-price-row">
                      <span className="oh-order-drawer-price-row__label">
                        {t("shipmentId", "ID")}
                      </span>
                      <span className="oh-order-drawer-price-row__value oh-order-drawer-price-row__value--mono">
                        {th.shipmentId}
                      </span>
                    </div>
                  ) : null}
                  {th.lastError || th.lastErrorMessage ? (
                    <div className="oh-order-drawer-price-row oh-order-drawer-price-row--error">
                      <span className="oh-order-drawer-price-row__label">
                        {t("shipmentError", "Shipment Error")}
                      </span>
                      <span className="oh-order-drawer-error-text">
                        {formatTrackHubError(th, { t, isRTL })}
                      </span>
                    </div>
                  ) : null}
                </>
              );
            })()}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default OrderDetailsTab;
